import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

// The "Added to your cart" pop-up (snippets/added-to-cart.liquid).
//
// Every add the theme makes is announced by the same event before its request
// leaves (assets/product-form.js, the quick add, the sticky bar, the Dispatch
// Date form), with a promise that settles once the cart has answered. The
// pop-up waits on that promise, so it never claims an add the shop refused,
// and then reads the cart to show what went in.
//
// Cowlendar's Book now is the booking app's own button: it posts to the cart
// with its own request and the theme hears nothing, which is how a booking
// used to leave the shopper on the same page wondering whether it worked. The
// bridge at the foot of this file watches for any request to /cart/add that
// the theme did not announce and announces it, so the header count, the
// petals and this pop-up all follow a booking the same way they follow an add.
// assets/booking-calendar.js has its own bridge for the calendar box; where
// that box is on the page this one stands aside so a booking is told once.

const dialog = /** @type {HTMLDialogElement | null} */ (document.getElementById('PpAdded'));

/** @param {number} cents */
function money(cents) {
  return new Intl.NumberFormat('en-NZ', { style: 'currency', currency: 'NZD' }).format(cents / 100);
}

/** @param {string} url @param {number} width */
function sized(url, width) {
  if (!url) return '';
  try {
    const u = new URL(url, window.location.href);
    u.searchParams.set('width', String(width));
    return u.toString();
  } catch (_) {
    return url;
  }
}

/**
 * The line that was just added: the newest line for that variant. The cart
 * lists the most recent line first.
 * @param {any[]} items
 * @param {string | number | undefined} variantId
 */
function addedLine(items, variantId) {
  if (!Array.isArray(items) || !items.length) return null;
  const id = String(variantId ?? '').split('/').pop();
  return items.find((item) => String(item.variant_id) === id) ?? items[0];
}

/** "18:00" or "06:00 PM" to "6pm"; "13:30" to "1:30pm". */
function clock(text) {
  const match = /^(\d{1,2}):(\d{2})\s*([AaPp][Mm])?$/.exec(text.trim());
  if (!match) return text.trim();
  let hour = Number(match[1]);
  let half = match[3]?.toLowerCase();
  if (!half) {
    half = hour >= 12 ? 'pm' : 'am';
    hour = hour % 12 || 12;
  }
  return `${hour}${match[2] === '00' ? '' : `:${match[2]}`}${half}`;
}

/**
 * Cowlendar's Date, "Oct 31, 2026, 01:30 PM - 03:00 PM (Pacific/Auckland)",
 * in the site's words: "Saturday 31 October, 1:30pm to 3pm". Anything it
 * cannot read is shown as it came.
 * @param {string} raw
 */
function bookingDate(raw) {
  const text = String(raw).replace(/\s*\(.*\)\s*$/, '');
  const [start, end] = text.split(' - ');
  const bits = (start || '').split(', ');
  if (bits.length < 3) return text;
  const day = new Date(`${bits[0]}, ${bits[1]}`);
  if (Number.isNaN(day.getTime())) return text;
  const weekday = day.toLocaleDateString('en-NZ', { weekday: 'long' });
  const month = day.toLocaleDateString('en-NZ', { month: 'long' });
  const times = end ? `${clock(bits[2])} to ${clock(end)}` : clock(bits[2]);
  return `${weekday} ${day.getDate()} ${month}, ${times}`;
}

/** The details a shopper chose, in the words they chose them in. */
function metaFor(item) {
  const parts = [];
  const props = item.properties || {};
  if (props.Date) parts.push(bookingDate(props.Date));
  if (props['Dispatch Date']) parts.push(`Dispatch Day: ${props['Dispatch Date']}`);
  if (!props.Date && item.variant_title && item.variant_title !== 'Default Title') parts.unshift(item.variant_title);
  if (item.quantity > 1) parts.push(`Quantity ${item.quantity}`);
  return parts.join(' · ');
}

/** @param {any} item @param {any} cart */
function fill(item, cart) {
  if (!dialog) return;
  const thumb = dialog.querySelector('[data-pp-added-thumb]');
  const name = dialog.querySelector('[data-pp-added-name]');
  const meta = dialog.querySelector('[data-pp-added-meta]');
  const price = dialog.querySelector('[data-pp-added-price]');
  const count = dialog.querySelector('[data-pp-added-count]');

  if (thumb) {
    thumb.textContent = '';
    if (item.image) {
      const img = document.createElement('img');
      img.src = sized(item.image, 160);
      img.alt = '';
      img.width = 72;
      img.height = 72;
      thumb.appendChild(img);
    }
  }
  if (name) name.textContent = item.product_title || item.title || '';
  if (meta) {
    const text = metaFor(item);
    meta.textContent = text;
    meta.hidden = !text;
  }
  if (price) price.textContent = item.final_line_price > 0 ? money(item.final_line_price) : 'Free';
  // A booking holds its place only until checkout, as the booking app says
  // in its own form; the pop-up repeats it so no one leaves thinking it's done.
  const note = dialog.querySelector('[data-pp-added-note]');
  if (note instanceof HTMLElement) note.hidden = !item.properties?.Date;
  if (count && cart) {
    const n = cart.item_count;
    count.textContent = `${n} ${n === 1 ? 'item' : 'items'} in your cart, ${money(cart.total_price)}`;
  }
}

function open() {
  if (!dialog) return;
  dialog.classList.remove('pp-added--in');
  if (!dialog.open) dialog.showModal();
  // One frame at rest first, so the bloom has a start to grow from.
  requestAnimationFrame(() => requestAnimationFrame(() => dialog.classList.add('pp-added--in')));
  /** @type {HTMLElement | null} */ (dialog.querySelector('[data-pp-added-checkout]'))?.focus({ preventScroll: true });
}

function close() {
  if (dialog?.open) dialog.close();
}

/** @param {string | number | undefined} variantId */
async function show(variantId) {
  const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
  if (!response.ok) return;
  const cart = await response.json();
  const item = addedLine(cart.items, variantId);
  if (!item) return;
  fill(item, cart);
  open();
}

if (dialog) {
  dialog.addEventListener('click', (event) => {
    const target = /** @type {Element} */ (event.target);
    // A press on the backdrop lands on the dialog itself, outside the panel.
    if (target === dialog || target.closest('[data-pp-added-close]')) close();
  });

  dialog.querySelector('[data-pp-added-cart]')?.addEventListener('click', (event) => {
    const drawer = document.querySelector('cart-drawer-component')?.closest('theme-drawer');
    if (!drawer || typeof (/** @type {any} */ (drawer).open) !== 'function') return;
    event.preventDefault();
    close();
    /** @type {any} */ (drawer).open();
  });

  dialog.addEventListener('close', () => dialog.classList.remove('pp-added--in'));

  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    const update = /** @type {any} */ (event);
    if (update.action !== 'add' || !update.promise) return;

    const variantId = update.lines?.[0]?.merchandiseId;
    // The quick add is a modal of its own; wait for it to close so the two
    // never sit on top of each other.
    const sourceModal = event.target instanceof Element ? event.target.closest('dialog:modal') : null;

    update.promise
      .then((/** @type {any} */ result) => {
        if (result?.detail?.didError) return;
        const go = () => show(variantId).catch(() => {});
        if (sourceModal && /** @type {HTMLDialogElement} */ (sourceModal).open) {
          sourceModal.addEventListener('close', go, { once: true });
        } else {
          go();
        }
      })
      .catch(() => {});
  });
}

// The bridge for adds the theme did not make (Cowlendar's Book now).

/** The theme's own add still in flight, so it is not answered twice. */
let themeAdd = Promise.resolve();
let syncing = Promise.resolve();

/** @param {PerformanceEntry} entry */
function isCartAdd(entry) {
  try {
    const path = new URL(entry.name, window.location.href).pathname;
    return path.endsWith('/cart/add') || path.endsWith('/cart/add.js');
  } catch (_) {
    return false;
  }
}

function shownCount() {
  const count = document.querySelector('cart-icon [ref="cartBubbleCount"]');
  const n = count ? parseInt(count.textContent ?? '', 10) : NaN;
  return Number.isNaN(n) ? 0 : n;
}

async function announceOutsideAdd() {
  await themeAdd;
  // The header writes its count a moment after the theme's add settles.
  await new Promise((resolve) => setTimeout(resolve, 300));
  const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
  if (!response.ok) return;
  const cart = await response.json();
  const added = cart.item_count - shownCount();
  if (added <= 0 || !cart.items?.length) return;

  const newest = cart.items[0];
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(newest.variant_id), quantity: added }],
      promise: deferred.promise,
    })
  );
  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: { items: cart.items, source: 'added-to-cart-bridge', didError: false },
  });
}

if (typeof PerformanceObserver !== 'undefined') {
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    const promise = /** @type {any} */ (event).promise;
    if (promise) themeAdd = promise.then(() => {}, () => {});
  });

  new PerformanceObserver((list) => {
    if (!list.getEntries().some(isCartAdd)) return;
    if (document.querySelector('booking-calendar')) return;
    syncing = syncing.then(announceOutsideAdd).catch(() => {});
  }).observe({ type: 'resource' });
}
